import React, { useState, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Upload,
  Space,
  Tag,
  Empty,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
  DownloadOutlined,
  FileTextOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import { toast } from "sonner";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import {
  createFileAttachement,
  deleteFileAttachementByID,
  getFileAttachementByEntityId,
} from "../../../../api/fileAttachement";
import { getAllStudentByClass } from "../../../../api/etudiants";
import Avvvatars from "avvvatars-react";
import {
  createHomework,
  deleteHomework,
  getHomeworkAndAnswerById,
  updateHomework,
} from "../../../../api/homework";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../../../core/common/autorisations/permissionsMap";
import { updateHomeworkAnswerByTeacher } from "../../../../api/homeworkAnswer";
import StudentAssignmentListFile from "./assignmantsFiles";
import { openSupportInNewTab } from "../../../../helper/supportHelper";

// Types
interface Assignment {
  id: string;
  title: string;
  description: string;
  deadline: string;
  createdAt: string;
  status: "active" | "closed";
  attachments: any[];
}

interface StudentResponse {
  id: string;
  studentId: string;
  studentName: string;
  studentAvatar?: string;
  submission_date?: string;
  is_late?: boolean;
  remark?: string;
  observation?: string;
  files: any[];
  status?: "submitted" | "pending" | "late";
}

// Zod Schema
const assignmentSchema = z.object({
  title: z.string().min(3, "Le titre doit contenir au moins 3 caractères"),
  description: z
    .string()
    .min(10, "La description doit contenir au moins 10 caractères"),
  deadline: z.any().refine((val) => val, "La date limite est requise"),
});

type AssignmentFormData = z.infer<typeof assignmentSchema>;

interface Props {
  courseId: string;
  classes: any[];
}

const AssignmentsManager: React.FC<Props> = ({ courseId, classes }) => {
  // State
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(
    null,
  );
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] =
    useState<Assignment | null>(null);

  const [selectedAssigementResponseFiles, setSelectedAssigementResponseFiles] =
    useState<any[]>([]);
  const [selectedStudentInfo, setSelectedStudentInfo] = useState<any>();

  const [allStudents, setAllStudents] = useState<any[]>([]);
  const [studentResponses, setStudentResponses] = useState<StudentResponse[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);

  // Evaluation State
  const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);
  const [currentResponseToEvaluate, setCurrentResponseToEvaluate] = useState<
    any | null
  >(null);
  const [evaluationObservation, setEvaluationObservation] = useState("");
  const [viewAssignmeentFiles, setViewAssignmeentFiles] =
    useState<boolean>(false);

  // Hook Form
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<AssignmentFormData>({
    resolver: zodResolver(assignmentSchema),
  });

  // Load mock data on mount
  useEffect(() => {
    fetchAssignments();
  }, []);

  // fetch assignments
  const fetchAssignments = async () => {
    try {
      setIsLoading(true);
      // Mock API call
      const response = await getHomeworkAndAnswerById(courseId);

      // console.log("assignement: ",response.data);

      // charger les réponses étudiantes
      setStudentResponses(response.data.answers);

      const formattedAssignments = await Promise.all(
        (response.data.course?.Homework || []).map(async (item: any) => {
          const res = await getFileAttachementByEntityId(item.id, "Homework");
          return {
            ...item,
            deadline: item.due_date,
            attachments: res.data || [],
            status: dayjs(item.due_date).isAfter(dayjs()) ? "active" : "closed",
          };
        }),
      );
      // console.log("assignement: ", formattedAssignments);

      setAssignments(formattedAssignments);
    } catch (error: any) {
      // console.log("Erreur de chargement des devoirs : ", error);
      toast.error("Erreur de chargement des devoirs");
    } finally {
      setIsLoading(false);
    }
  };

  // Handlers
  const handleOpenCreate = () => {
    setEditingAssignment(null);
    reset({ title: "", description: "", deadline: null });
    setFileList([]);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (assignment: Assignment) => {
    setEditingAssignment(assignment);
    setValue("title", assignment.title);
    setValue("description", assignment.description);
    setValue("deadline", dayjs(assignment.deadline));
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteHomework(id);
      fetchAssignments();
      toast.success("Devoir supprimé avec succès");
    } catch (error: any) {
      // console.log("erreur lors de la suppression du devoir : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const onSubmit = async (data: AssignmentFormData) => {
    try {
      setIsLoading(true);

      const formData = new FormData();

      if (fileList.length > 0) {
        for (const file of fileList) {
          formData.append("files", file.originFileObj);
        }
      }

      if (editingAssignment) {
        const res = await updateHomework({
          id: editingAssignment.id,
          id_course: courseId,
          title: data.title,
          description: data.description,
          due_date: dayjs(data.deadline).format("YYYY-MM-DD HH:mm"),
        });

        if (fileList.length > 0) {
          formData.append("entity_id", res.data.id);
          formData.append("entity_type", "Homework");

          await createFileAttachement(formData);
        }
        toast.success("Devoir/TP modifié avec succès");
      } else {
        const res = await createHomework({
          id_course: courseId,
          title: data.title,
          description: data.description,
          due_date: dayjs(data.deadline).format("YYYY-MM-DD HH:mm"),
        });

        if (fileList.length > 0) {
          formData.append("entity_id", res.data.id);
          formData.append("entity_type", "Homework");

          await createFileAttachement(formData);
        }
        toast.success("Devoir/TP créé avec succès");
      }
      // recharger les devoirs après la création ou la modification
      fetchAssignments();
      setIsModalOpen(false);
    } catch (error: any) {
      // console.error("Erreur lors de la création du Devoir/Tp: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur est survenue",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEvaluation = (response: StudentResponse) => {
    setCurrentResponseToEvaluate(response);
    setEvaluationObservation(response.observation || "");
    setIsEvaluationModalOpen(true);
  };

  const handleSaveEvaluation = async () => {
    if (!currentResponseToEvaluate) return;

    try {
      await updateHomeworkAnswerByTeacher(currentResponseToEvaluate.id, {
        observation: evaluationObservation,
      });

      fetchAssignments();
      toast.success("Observation enregistrée");
      setIsEvaluationModalOpen(false);
    } catch (error: any) {
      // console.error("Erreur lors de l'enregistrement de l'évaluation: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const handleViewResponses = async (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setIsResponseModalOpen(true);
    try {
      // Assuming classes prop has { id_class }
      if (classes && classes.length > 0) {
        const promises = classes.map((cls) => getAllStudentByClass(cls.id));
        const results = await Promise.all(promises);
        results.forEach((res) => {
          if (res && res.data) {
            setAllStudents((prev) => [...prev, ...res.data]);
          }
        });
      }
    } catch (error: any) {
      // console.error("Erreurlors du chargement des étudiants: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const handleDeleteAssignementFile = async (fileId: string) => {
    try {
      await deleteFileAttachementByID(
        fileId,
        buildPermissionContext(Resource.FILE_ATTACHEMENT, Action.DELETE),
      );
      toast.success("Fichier supprimé");
      fetchAssignments();
    } catch (error: any) {
      // console.log("Erreur lors de la suppression du fichier: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const handleGetAssignementFile = async (fileName: string) => {
    try {
      await openSupportInNewTab({ entityType: "Homework", fileName });
    } catch (error: any) {
      // console.log("Erreur lors de l'ouverture du fichier: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };
  // Columns for Table
  const columns = [
    {
      title: "Titre",
      dataIndex: "title",
      key: "title",
      render: (text: string, record: Assignment) => (
        <div>
          <div className="fw-bold">{text}</div>
          <small className="text-muted">
            {record.description.substring(0, 50)}...
          </small>
        </div>
      ),
    },
    {
      title: "Date limite",
      dataIndex: "deadline",
      key: "deadline",
      render: (date: string) => (
        <Tag color={dayjs(date).isBefore(dayjs()) ? "red" : "green"}>
          {dayjs(date).format("DD/MM/YYYY HH:mm")}
        </Tag>
      ),
    },
    {
      title: "Fichiers",
      dataIndex: "attachments",
      key: "attachments",
      render: (files: any[], record: Assignment) => (
        <div>
          <span>{files.length} fichier(s)</span>
          {files.length > 0 && (
            <div className="mt-1">
              {files.map((file) => (
                <div key={file.id} className="d-flex align-items-center gap-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{file.filename}</Tooltip>}
                  >
                    <div>
                      <Button
                        type="link"
                        size="small"
                        icon={<FileTextOutlined />}
                        onClick={() => handleGetAssignementFile(file.filename)}
                      >
                        {file.filename.length > 20
                          ? `${file.filename.slice(0, 15)}…`
                          : file.filename}
                      </Button>
                    </div>
                  </OverlayTrigger>
                  <Popconfirm
                    title="Êtes-vous sûr de vouloir supprimer ce fichier ?"
                    onConfirm={async () => handleDeleteAssignementFile(file.id)}
                    okText="Oui"
                    cancelText="Non"
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                    />
                  </Popconfirm>
                </div>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Assignment) => (
        <Space>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Voir les réponses</Tooltip>}
          >
            <Button
              icon={<EyeOutlined />}
              onClick={() => handleViewResponses(record)}
            />
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Modifier</Tooltip>}>
            <Button
              icon={<EditOutlined />}
              onClick={() => handleOpenEdit(record)}
            />
          </OverlayTrigger>
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer ce devoir ?"
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (!isModalOpen && fileList.length > 0) {
      // vider le fileList
      setFileList([]);
    }
  }, [isModalOpen]);

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Devoirs & TD</h4>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleOpenCreate}
        >
          Nouveau Devoir
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={assignments}
        rowKey="id"
        locale={{ emptyText: <Empty description="Aucun devoir créé" /> }}
      />

      {/* Create/Edit Modal */}
      <Modal
        title={
          editingAssignment ? "Modifier le devoir" : "Créer un nouveau devoir"
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={700}
      >
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <Form.Item
            label="Titre"
            validateStatus={errors.title ? "error" : ""}
            help={errors.title?.message}
          >
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Input {...field} placeholder="Ex: Devoir de Mathématiques" />
              )}
            />
          </Form.Item>

          <Form.Item
            label="Description"
            validateStatus={errors.description ? "error" : ""}
            help={errors.description?.message}
          >
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Input.TextArea
                  {...field}
                  rows={4}
                  placeholder="Instructions détaillées..."
                />
              )}
            />
          </Form.Item>

          <Form.Item
            label="Date limite"
            validateStatus={errors.deadline ? "error" : ""}
            help={errors.deadline?.message?.toString()}
          >
            <Controller
              name="deadline"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  className="w-100"
                  placeholder="Sélectionner une date"
                />
              )}
            />
          </Form.Item>

          <Form.Item label="Fichiers joints">
            <Upload
              beforeUpload={() => false} // Prevent auto upload
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
              multiple
            >
              <Button icon={<UploadOutlined />}>
                Sélectionner des fichiers
              </Button>
            </Upload>
          </Form.Item>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button onClick={() => setIsModalOpen(false)}>Annuler</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {editingAssignment ? "Enregistrer" : "Créer"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* View Responses Modal */}
      <Modal
        title={`Réponses - ${selectedAssignment?.title}`}
        open={isResponseModalOpen}
        onCancel={() => setIsResponseModalOpen(false)}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setIsResponseModalOpen(false)}>
            Fermer
          </Button>,
        ]}
      >
        <div
          className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-2"
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          {studentResponses.map((record: any) => {
            const student = allStudents.find(
              (s) => s.id_studentProfil === record?.id_studentProfil,
            );
            const name = student?.StudentProfil
              ? `${student.StudentProfil.firstname || ""} ${
                  student.StudentProfil.lastname || ""
                }`.trim()
              : "";
            const matricule = student?.StudentProfil?.matricule || "";
            const hasSubmission = !!record.submission_date;
            const isLate = !!record.is_late;
            const statusText = hasSubmission
              ? isLate
                ? "En retard"
                : "Rendu"
              : "Non rendu";
            const borderClass = hasSubmission
              ? isLate
                ? "border-warning"
                : "border-success"
              : "border-secondary";
            return (
              <>
                <div key={record.id} className="col">
                  <div
                    className={`card h-100 border-2 ${borderClass} shadow-sm`}
                  >
                    <div className="card-body p-3 d-flex flex-column">
                      <div className="d-flex align-items-center mb-2">
                        <Avvvatars value={name} style="character" size={40} />
                        <div className="ms-2 d-flex flex-column">
                          <span
                            className="fw-bold text-truncate"
                            title={name}
                            style={{ fontSize: "0.95rem" }}
                          >
                            {name}
                          </span>
                          <span
                            className="text-muted"
                            style={{ fontSize: "0.75rem" }}
                          >
                            {matricule}
                          </span>
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <Tag
                          color={
                            hasSubmission
                              ? isLate
                                ? "warning"
                                : "success"
                              : "default"
                          }
                        >
                          {statusText}
                        </Tag>
                        <span
                          className="text-muted"
                          style={{ fontSize: "0.8rem" }}
                        >
                          {hasSubmission
                            ? dayjs(record.submission_date).format(
                                "DD/MM/YYYY HH:mm",
                              )
                            : "-"}
                        </span>
                      </div>
                      <div className="mt-auto d-flex justify-content-between gap-2">
                        {hasSubmission && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Évaluer</Tooltip>}
                          >
                            <Button
                              icon={<MessageOutlined />}
                              size="small"
                              onClick={() => handleOpenEvaluation(record)}
                            />
                          </OverlayTrigger>
                        )}
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Voir fichiers</Tooltip>}
                        >
                          <Button
                            icon={<DownloadOutlined />}
                            size="small"
                            onClick={() => {
                              setSelectedStudentInfo(student?.StudentProfil);
                              setSelectedAssigementResponseFiles(record.files);
                              setViewAssignmeentFiles(true);
                            }}
                          />
                        </OverlayTrigger>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          })}
          {studentResponses.length === 0 && (
            <div className="col-12">
              <div className="text-center p-5 text-muted">
                Aucune réponse pour ce devoir.
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Observation Modal */}
      <Modal
        title={`Observation - ${(() => {
          const student = allStudents.find(
            (student: any) =>
              student.id_studentProfil ===
              currentResponseToEvaluate?.id_studentProfil,
          );
          return student?.StudentProfil
            ? `${student.StudentProfil.firstname || ""} ${
                student.StudentProfil.lastname || ""
              }`.trim()
            : "";
        })()}`}
        open={isEvaluationModalOpen}
        onCancel={() => setIsEvaluationModalOpen(false)}
        onOk={handleSaveEvaluation}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <div className="mb-3">
          <label className="form-label fw-bold">Remarque de l'étudiant :</label>
          <div className="p-2 bg-light rounded fst-italic text-muted">
            {currentResponseToEvaluate?.remark || "Aucune remarque"}
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label fw-bold">
            Observation (Professeur) :
          </label>
          <Input.TextArea
            rows={4}
            value={evaluationObservation}
            onChange={(e) => setEvaluationObservation(e.target.value)}
            placeholder="Saisissez votre observation ici..."
          />
        </div>
      </Modal>

      {/* View Assignment Files Modal */}
      <StudentAssignmentListFile
        show={viewAssignmeentFiles}
        onClose={() => setViewAssignmeentFiles(false)}
        files={selectedAssigementResponseFiles}
        student={selectedStudentInfo}
      />
    </div>
  );
};

export default AssignmentsManager;
